import { debug } from "debug";
import { invariant } from "ts-invariant";

const log = debug("studio:sketch:rasterize-pdf");

declare global {
  interface Map<K, V> {
    getOrInsertComputed(key: K, callbackFn: (key: K) => V): V;
  }
}

// Polyfill Map.prototype.getOrInsertComputed (TC39 Stage 3, Chrome 134+)
// Required by pdfjs-dist 5.x.
if (!Map.prototype.getOrInsertComputed) {
  Object.defineProperty(Map.prototype, "getOrInsertComputed", {
    value: function (key: unknown, callbackFn: (key: unknown) => unknown) {
      if (this.has(key)) return this.get(key);
      const value = callbackFn(key);
      this.set(key, value);
      return value;
    },
    writable: true,
    configurable: true,
    enumerable: false,
  });
}

// Lazy-load pdfjs-dist so the polyfill above is guaranteed to run first.
let _pdfjsLib: typeof import("pdfjs-dist") | null = null;
async function getPdfjsLib() {
  if (!_pdfjsLib) {
    _pdfjsLib = await import("pdfjs-dist");
    const workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url"))
      .default;
    _pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
    log("loaded pdfjs-dist, workerSrc: %s", workerSrc);
  }
  return _pdfjsLib;
}

const MAX_DIMENSION = 8000;

export interface RasterizeResult {
  blob: Blob;
  width: number;
  height: number;
  /** Physical page width in inches (from PDF metadata at 72 DPI) */
  pageWidthInches: number;
  /** Physical page height in inches */
  pageHeightInches: number;
}

export async function rasterizePdfPage(
  source: File | ArrayBuffer | string,
  pageNum = 1,
  dpi = 150,
): Promise<RasterizeResult> {
  const pdfjsLib = await getPdfjsLib();

  let data: ArrayBuffer | string;
  if (typeof source === "string") {
    const res = await fetch(source);
    data = await res.arrayBuffer();
  } else if (
    typeof source === "object" &&
    source !== null &&
    "arrayBuffer" in source &&
    typeof source.arrayBuffer === "function"
  ) {
    data = await source.arrayBuffer();
  } else {
    invariant(
      source instanceof ArrayBuffer,
      "Expected source to be an ArrayBuffer",
    );
    data = source;
  }
  log(
    "loading document, data size: %o",
    data instanceof ArrayBuffer ? data.byteLength : typeof data,
  );
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  log("document loaded, pages: %d", pdf.numPages);
  const page = await pdf.getPage(pageNum);

  const baseViewport = page.getViewport({ scale: 1 });
  log("page viewport: %d x %d", baseViewport.width, baseViewport.height);
  // PDF default is 72 DPI
  let scale = dpi / 72;

  // Cap max dimension
  const rawWidth = baseViewport.width * scale;
  const rawHeight = baseViewport.height * scale;
  if (rawWidth > MAX_DIMENSION || rawHeight > MAX_DIMENSION) {
    const capScale = MAX_DIMENSION / Math.max(rawWidth, rawHeight);
    scale *= capScale;
  }

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  log("canvas size: %d x %d", canvas.width, canvas.height);
  const ctx = canvas.getContext("2d")!;

  await page.render({ canvas, canvasContext: ctx, viewport }).promise;
  log("render complete");

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob failed"))),
      "image/png",
    );
  });
  log("blob size: %d bytes", blob.size);
  return {
    blob,
    width: canvas.width,
    height: canvas.height,
    pageWidthInches: baseViewport.width / 72,
    pageHeightInches: baseViewport.height / 72,
  };
}
